import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const changePassword = hooks.useChangePassword();
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (values.newPassword.length < 6) {
      toast.error(t('portal.account.passwordMinLength'));
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      toast.error(t('portal.account.passwordMismatch'));
      return;
    }
    try {
      await changePassword.mutateAsync({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      toast.success(t('portal.account.passwordUpdated'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.account.changePassword')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="old-password">{t('portal.account.currentPassword')}</Label>
            <Input
              id="old-password"
              type="password"
              autoComplete="current-password"
              {...register('oldPassword', { required: true })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">{t('portal.account.newPassword')}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              {...register('newPassword', { required: true })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">{t('portal.account.confirmPassword')}</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword', { required: true })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || changePassword.isPending}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
